import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiContext, API_BASE, handleResponse } from "@/contexts/ApiContext";
import type { Document, CreateDocumentInput, PaginatedResult, DocumentListItem, TrashDocumentItem } from "@/lib/api";

export interface UseDocumentsPaginatedParams {
  page?: number;
  pageSize?: number;
  search?: string;
  labelId?: string;
  sortBy?: "title" | "createdAt" | "updatedAt";
  sortDir?: "asc" | "desc";
  /** mine: the documents you own; shared: someone else's, shared with you; unset: both. */
  scope?: "mine" | "shared";
  enabled?: boolean;
}

export function useDocumentsPaginated(params: UseDocumentsPaginatedParams = {}) {
  const { fetchWithAuth } = useApiContext();
  const {
    page = 1,
    pageSize = 20,
    search,
    labelId,
    sortBy = "updatedAt",
    sortDir = "desc",
    scope,
    enabled = true,
  } = params;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["documents", "paginated", { page, pageSize, search, labelId, sortBy, sortDir, scope }],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set("page", String(page));
      searchParams.set("pageSize", String(pageSize));
      if (search) searchParams.set("search", search);
      if (labelId) searchParams.set("labelId", labelId);
      searchParams.set("sortBy", sortBy);
      searchParams.set("sortDir", sortDir);
      if (scope) searchParams.set("scope", scope);

      const response = await fetchWithAuth(`${API_BASE}/documents?${searchParams.toString()}`);
      return handleResponse<PaginatedResult<DocumentListItem>>(response);
    },
    enabled,
    // An empty answer is final (3 Nov 2026). The list used to ask again three times, 3 s apart, showing
    // "Loading your documents…", in case starter documents were still being copied: every visit by an
    // account with no documents took 6–9 s (the owner, on mobile). The copy runs in the sign-in sync,
    // before the first list answers, and never while plan limits are on (production, the test copy).
  });

  return {
    documents: data?.items ?? [],
    page: data?.page ?? 1,
    pageSize: data?.pageSize ?? pageSize,
    totalCount: data?.totalCount ?? 0,
    totalPages: data?.totalPages ?? 0,
    isLoading,
    error,
    refetch,
  };
}

// Legacy hook for backwards compatibility - now returns items from first page
export function useDocuments() {
  const { fetchWithAuth } = useApiContext();

  const { data, isLoading, error } = useQuery({
    queryKey: ["documents"],
    queryFn: async () => {
      const response = await fetchWithAuth(`${API_BASE}/documents?pageSize=100`);
      const result = await handleResponse<PaginatedResult<DocumentListItem>>(response);
      // Map to old Document format for backwards compatibility
      return result.items.map(item => ({
        id: item.id,
        title: item.title,
        ownerId: item.ownerId,
        ownerName: item.ownerName,
        teamId: item.teamId,
        teamName: item.teamName,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        lastOpenedAt: item.lastOpenedAt,
        blockCount: item.blockCount,
        labels: item.labels,
        role: item.role,
      }));
    },
  });

  return {
    documents: data,
    isLoading,
    error,
  };
}

export function useCreateDocument() {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();

  return useMutation({
    mutationFn: async (data: CreateDocumentInput) => {
      const response = await fetchWithAuth(`${API_BASE}/documents`, {
        method: "POST",
        body: JSON.stringify(data),
      });
      const document = await handleResponse<Document>(response);
      return { document };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["plan"] });
    },
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetchWithAuth(`${API_BASE}/documents/${id}`, {
        method: "DELETE",
      });
      if (response.ok) {
        return { success: true };
      }
      throw new Error("Failed to delete document");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["plan"] });
    },
  });
}

export function useTrashDocuments(page = 1, pageSize = 20) {
  const { fetchWithAuth } = useApiContext();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["documents", "trash", { page, pageSize }],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      const response = await fetchWithAuth(`${API_BASE}/documents/trash?${params}`);
      return handleResponse<PaginatedResult<TrashDocumentItem>>(response);
    },
  });

  return {
    documents: data?.items ?? [],
    totalCount: data?.totalCount ?? 0,
    totalPages: data?.totalPages ?? 0,
    page: data?.page ?? 1,
    isLoading,
    error,
    refetch,
  };
}

export function useRestoreDocument() {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetchWithAuth(`${API_BASE}/documents/${id}/restore`, {
        method: "POST",
      });
      if (response.ok) return { success: true };
      throw new Error("Failed to restore document");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["plan"] });
    },
  });
}

export function usePermanentDeleteDocument() {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetchWithAuth(`${API_BASE}/documents/${id}/permanent`, {
        method: "DELETE",
      });
      if (response.ok) return { success: true };
      throw new Error("Failed to permanently delete document");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["plan"] });
    },
  });
}

export function useDuplicateDocument() {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetchWithAuth(`${API_BASE}/documents/${id}/duplicate`, {
        method: "POST",
      });
      const document = await handleResponse<Document>(response);
      return { document };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["plan"] });
    },
  });
}
